import { slicedToArray as _slicedToArray } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import i18n from 'i18next';
import { t } from '../../../i18n/translate.js';
import { isNameValid, isPhoneNumberValid, isEmailValid } from '../../../services/utilities/validation.js';
import { cleanPhoneNumber } from '../../../services/utilities/phoneNumberUtil.js';
import '../../../components/Button/Button.js';
import '../../../components/AppLogo/AppLogo.js';
import '../../../components/Avatar/Avatar.js';
import Input from '../../../components/Input/Input.js';
import '../../../components/Input/PasswordInput.js';
import '../../../components/Icon/Icon.js';
import '../../../components/Image/Image.js';
import 'classnames';
import '../../../components/LoadingSpinner/LoadingSpinner.module.scss.js';
import '../../../components/Note/Note.js';
import '../../../components/RadioButton/RadioButton.js';
import '../../../components/RoundButton/RoundButton.js';
import '../../../components/RoundButton/RoundButtonElement/RoundButtonElement.js';
import '../../../components/Expandable/Expandable.js';
import '../../../components/PopOver/PopOver.js';
import '../../../components/Wizard/Wizard.js';
import '../../../components/Wizard/WizardStepContent/WizardStepContent.js';
import '../../../components/Wizard/NavigationBar/NavigationBar.js';
import '../../../components/ToggleButton/ToggleButton.js';
import '../../../components/ToggleButton/ToggleButtonElement/ToggleButtonElement.js';
import '../../../components/Wizard/ProgressBar/ProgressBar.js';
import '../../../components/Checkbox/Checkbox.js';
import '../../../components/SelectionField/SelectionField.js';
import '../../../components/Card/Card.js';
import '../../../components/Badge/Badge.js';
import '../../../components/SettingsItem/SettingsItem.js';
import '../../../components/SortableList/SortableList.js';
import '../../../components/Contacts/ContactItem.js';
import '../../../components/TopElement/TopElement.js';
import '../../../components/SidePanelInfoBox/SidePanelInfoBox.js';
import InternationalPhonePicker from '../../../components/InternationalPhonePicker/InternationalPhonePicker.js';
import '../../../components/Carousel/ImageCarousel.module.scss.js';
import { NAME_MAX_LENGTH, PHONE_MAX_LENGTH } from '../../../services/appConstants/appConstants.js';
import styles from './UserForm.module.scss.js';

function UserForm(_ref) {
  var onChange = _ref.onChange,
    user = _ref.user,
    isReadOnly = _ref.isReadOnly,
    showEditLink = _ref.showEditLink,
    dataTestId = _ref.dataTestId,
    className = _ref.className,
    supportedDialCodes = _ref.supportedDialCodes;
  var _useState = useState(user.name),
    _useState2 = _slicedToArray(_useState, 2),
    name = _useState2[0],
    setName = _useState2[1];
  var _useState3 = useState(user.mobilePhoneNumber),
    _useState4 = _slicedToArray(_useState3, 2),
    mobile = _useState4[0],
    setMobile = _useState4[1];
  var _useState5 = useState(user.phoneNumber),
    _useState6 = _slicedToArray(_useState5, 2),
    phone = _useState6[0],
    setPhone = _useState6[1];
  var _useState7 = useState(user.email),
    _useState8 = _slicedToArray(_useState7, 2),
    email = _useState8[0],
    setEmail = _useState8[1];
  var _useState9 = useState({}),
    _useState10 = _slicedToArray(_useState9, 2),
    formErrorMap = _useState10[0],
    setFormErrorMap = _useState10[1];
  var hasAccessLevel = user.profile !== 'NONE';
  var hasCallOrder = user.callOrder > 0;
  var requiredContactFields = hasAccessLevel || hasCallOrder;
  var namePlaceholder = "".concat(t('MPLIB_USER_CREATE_NAME_TITLE'), " *");
  var mobilePlaceholder = requiredContactFields ? "".concat(t('MPLIB_USER_CREATE_MOBILE_TITLE'), " *") : t('MPLIB_USER_CREATE_MOBILE_TITLE');
  var phonePlaceholder = t('MPLIB_USER_CREATE_PHONE_TITLE');
  var emailPlaceholder = hasAccessLevel ? "".concat(t('MPLIB_USER_CREATE_EMAIL_TITLE'), " *") : t('MPLIB_USER_CREATE_EMAIL_TITLE');
  var isFormValid = function isFormValid(errorMap) {
    return Object.keys(errorMap).length === 0;
  };
  var _useState11 = useState(user.mobilePhonePrefix),
    _useState12 = _slicedToArray(_useState11, 2),
    phonePrefix = _useState12[0],
    setPhonePrefix = _useState12[1];
  var hasRequiredFormFields = function hasRequiredFormFields(userInput) {
    if (!hasAccessLevel && !hasCallOrder) {
      return userInput.name !== '';
    }
    if (!hasAccessLevel && hasCallOrder) {
      return userInput.name !== '' && userInput.mobilePhoneNumber !== '';
    }
    return userInput.name && userInput.mobilePhoneNumber && userInput.email;
  };
  var inputHasError = function inputHasError(inputField) {
    return Object.hasOwnProperty.call(formErrorMap, inputField);
  };
  var inputNeedsValue = function inputNeedsValue(inputField) {
    if (isReadOnly) {
      return null;
    }
    switch (inputField) {
      case 'name':
        return !name;
      case 'mobile':
        return requiredContactFields && !mobile;
      case 'email':
        return hasAccessLevel && !email;
      case 'prefix':
        return hasAccessLevel && !phonePrefix || mobile && !phonePrefix;
      default:
        return false;
    }
  };
  var getErrorMessage = function getErrorMessage(inputField) {
    var msg = null;
    if (isReadOnly) {
      return null;
    }
    switch (inputField) {
      case 'name':
        if (inputHasError('name')) {
          msg = t('MPLIB_USERS_USERNAME_INVALID');
        }
        break;
      case 'mobile':
        if (inputNeedsValue(inputField)) {
          msg = t('MPLIB_USERS_PHONE_REQUIRED_ACCESS_AND_OPERATOR');
        } else if (inputHasError(inputField)) {
          msg = t('MPLIB_CONTACT_EDIT_ERROR_INVALID_PHONE_NUMBER');
        }
        break;
      case 'email':
        if (inputNeedsValue(inputField)) {
          msg = t('MPLIB_USERS_EMAIL_REQUIRED_ACCESS_AND_OPERATOR');
        } else if (inputHasError(inputField)) {
          msg = t('MPLIB_CONTACT_EDIT_ERROR_INVALID_EMAIL');
        }
        break;
      case 'prefix':
        if (inputNeedsValue(inputField)) {
          msg = t('MPLIB_CONTACT_EDIT_ERROR_NO_PHONE_PREFIX');
        }
        break;
      case 'phone':
        if (inputHasError(inputField)) {
          msg = t('MPLIB_CONTACT_EDIT_ERROR_INVALID_PHONE_NUMBER');
        }
        break;
      default:
        return null;
    }
    return msg;
  };
  var handleChange = function handleChange(userInput) {
    var newFormErrorMap = {};
    if (userInput.name && !isNameValid(userInput.name)) {
      newFormErrorMap.name = true;
    }
    if (userInput.mobilePhoneNumber && !isPhoneNumberValid(userInput.mobilePhoneNumber) || hasAccessLevel && !userInput.mobilePhoneNumber && userInput.mobilePhonePrefix) {
      newFormErrorMap.mobile = true;
    }
    if (userInput.mobilePhoneNumber && !userInput.mobilePhonePrefix) {
      newFormErrorMap.mobilePhonePrefix = true;
    }
    if (userInput.phoneNumber && !isPhoneNumberValid(userInput.phoneNumber)) {
      newFormErrorMap.phone = true;
    }
    if (userInput.email && !isEmailValid(userInput.email)) {
      newFormErrorMap.email = true;
    }
    setFormErrorMap(newFormErrorMap);
    onChange({
      userInput: userInput,
      valid: isFormValid(newFormErrorMap) && hasRequiredFormFields(userInput)
    });
  };
  var validatePhone = function validatePhone(value) {
    var cleanPhone = cleanPhoneNumber(value);
    setMobile(cleanPhone);
    handleChange({
      name: name,
      email: email,
      mobilePhoneNumber: cleanPhone,
      phoneNumber: phone,
      mobilePhonePrefix: phonePrefix
    });
  };
  var validateAlternative = function validateAlternative(value) {
    var cleanPhone = cleanPhoneNumber(value);
    handleChange({
      name: name,
      email: email,
      mobilePhoneNumber: mobile,
      phoneNumber: cleanPhone,
      mobilePhonePrefix: phonePrefix
    });
  };
  useEffect(function () {
    if (!user) {
      handleChange({
        name: name,
        email: email,
        mobilePhoneNumber: mobile,
        phoneNumber: phone
      });
    } else {
      setName(user.name);
      setMobile(user.mobilePhoneNumber);
      setPhone(user.phoneNumber);
      setEmail(user.email);
      setPhonePrefix(user.mobilePhonePrefix);
    }
  }, [user]);
  var handlePrefixChange = function handlePrefixChange(prefix) {
    setPhonePrefix(prefix);
    handleChange({
      name: name,
      email: email,
      mobilePhoneNumber: mobile,
      phoneNumber: phone,
      mobilePhonePrefix: prefix
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    id: dataTestId,
    className: className
  }, /*#__PURE__*/React.createElement(Input, {
    disabled: isReadOnly,
    className: styles.userInputField,
    onChange: function onChange(value) {
      if (value.length <= NAME_MAX_LENGTH) {
        setName(value);
        handleChange({
          name: value,
          mobilePhoneNumber: mobile,
          phoneNumber: phone,
          email: email,
          mobilePhonePrefix: phonePrefix
        });
      }
    },
    placeholder: namePlaceholder,
    value: name,
    hasError: inputHasError('name') || inputNeedsValue('name'),
    dataTestId: "nameInput",
    errorMessage: getErrorMessage('name')
  }), /*#__PURE__*/React.createElement(InternationalPhonePicker, {
    setSelectedCountryPhone: handlePrefixChange,
    selectedCountryPhone: phonePrefix,
    phoneNumber: mobile,
    setPhoneNumber: validatePhone,
    phoneError: inputHasError('mobile') || inputNeedsValue('mobile') || inputNeedsValue('prefix'),
    placeholder: mobilePlaceholder,
    dropDownBelowButton: true,
    errorMessage: getErrorMessage('prefix') || getErrorMessage('mobile'),
    disabled: isReadOnly,
    supportedDialCodes: supportedDialCodes
  }), /*#__PURE__*/React.createElement(Input, {
    disabled: isReadOnly,
    onChange: function onChange(value) {
      validateAlternative(value);
    },
    maxLength: PHONE_MAX_LENGTH,
    placeholder: phonePlaceholder,
    value: phone,
    hasError: inputHasError('phone'),
    dataTestId: "phoneInput",
    errorMessage: getErrorMessage('phone'),
    type: "tel"
  }), /*#__PURE__*/React.createElement(Input, {
    disabled: isReadOnly,
    type: "email",
    inputMode: "email",
    onChange: function onChange(value) {
      setEmail(value);
      handleChange({
        name: name,
        mobilePhoneNumber: mobile,
        phoneNumber: phone,
        mobilePhonePrefix: phonePrefix,
        email: value
      });
    },
    placeholder: emailPlaceholder,
    value: email,
    hasError: inputHasError('email') || inputNeedsValue('email'),
    dataTestId: "emailInput",
    errorMessage: getErrorMessage('email')
  }), isReadOnly && !showEditLink && /*#__PURE__*/React.createElement("p", {
    className: "error"
  }, t('MPLIB_EDIT_OTHER_USER_ATTENTION')), showEditLink && /*#__PURE__*/React.createElement("div", {
    className: styles.accountSettingsLink
  }, /*#__PURE__*/React.createElement("a", {
    href: "/".concat(i18n.language, "/accountinformation.html")
  }, t('MPLIB_EDIT_ACCOUNT_SETTINGS'))));
}
var initialState = {
  name: '',
  mobilePhoneNumber: '',
  email: '',
  profile: 'NONE',
  callOrder: 0
};
UserForm.propTypes = {
  user: PropTypes.shape({
    name: PropTypes.string,
    mobilePhoneNumber: PropTypes.string,
    phoneNumber: PropTypes.string,
    email: PropTypes.string,
    profile: PropTypes.string,
    callOrder: PropTypes.number,
    mobilePhonePrefix: PropTypes.string
  }),
  onChange: PropTypes.func.isRequired,
  isReadOnly: PropTypes.bool,
  showEditLink: PropTypes.bool,
  dataTestId: PropTypes.string,
  className: PropTypes.string,
  supportedDialCodes: PropTypes.arrayOf(PropTypes.string)
};
UserForm.defaultProps = {
  isReadOnly: false,
  showEditLink: false,
  dataTestId: null,
  className: '',
  user: initialState,
  supportedDialCodes: null
};

export { UserForm as default };
